import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { QueryParams } from '../core/http/query-params';
import { Observable, defer } from 'rxjs';
import { map, switchMap } from 'rxjs/operators';

import {
  PagedQuery,
  SummaryPagedQuery,
  SummaryWeeklyPagedQuery,
} from '@/app/models';
import {
  HTTP_WORD,
  MetricsApiService,
  RdoHttpService,
  RevenueMappingService,
} from '@/app/core';
import {
  QueryService,
  ViewService,
  ComparisonModeSortOptions,
} from '@/app/core/query';
import { AuthenticationService } from '@/app/core/authentication/authentication.service';
import { FilterInfoService } from '@/app/filter/services/filter-info.service';
import * as filterFunctions from '@/app/filter/functions/filter.functions';
import { FeatureFlagService } from '@/app/core/feature-flags/feature-flag.service';
import { FilteredMetricsApiService } from '@/app/core/services/filtered-metrics-api.service';
import { ActiveFilterService } from '@/app/core/query/active-filter.service';
import { RequestCancellationService } from '@/app/core/http/request-cancellation.service';
import { environment } from '@/environments/environment';
import { normalizePercentNullsToZero } from '@/app/core/utils/normalize-nulls';

@Injectable()
export class SummaryService {
  private isGetRequest = false;
  private metricsApiService: FilteredMetricsApiService;

  constructor(
    private http: HttpClient,
    private rdoHttp: RdoHttpService,
    private revenueMappingService: RevenueMappingService,
    private viewService: ViewService,
    private queryService: QueryService,
    private filterInfoService: FilterInfoService,
    private featureFlagService: FeatureFlagService,
    private authenticationService: AuthenticationService,
    private activeFilterService: ActiveFilterService,
    private requestCancellationService: RequestCancellationService
  ) {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    this.metricsApiService = new FilteredMetricsApiService(
      this.http,
      this.authenticationService,
      this.activeFilterService,
      this.requestCancellationService
    );
  }

  public getCards = (): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    if (this.isGetRequest) {
      return this.rdoHttp.getf('metrics/summary/cards').pipe(
        map((p) => {
          return (p as any).Items[0];
        })
      );
    } else {
      return this.rdoHttp.post('metrics/summary/cards').pipe(
        map((p) => {
          return (p as any).Items[0];
        })
      );
    }
  };

  public getChartData = (
    monthsToReturn: number,
    useRouseSchema: boolean
  ): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    if (this.isGetRequest) {
      // for some reason api endpoint requires these
      const parameters = new QueryParams();
      parameters.set('page', '1');
      parameters.set('pageSize', '500');
      parameters.set('MonthsToReturn', monthsToReturn.toString());
      if (useRouseSchema) {
        parameters.set('UseRouseSchema', 'true');
      }

      // remove DateFrom and DateTo from global filter query so we can used cached version
      return this.rdoHttp
        .getfx(['MonthIDStart', 'MonthIDEnd'], 'metrics/summary/chart', {
          search: parameters,
        })
        .pipe(
          map((p) => {
            return (p as any).Items;
          })
        );
    } else {
      const pagedSorted = new SummaryPagedQuery();
      pagedSorted.Page = 1;
      pagedSorted.PageSize = 500;
      pagedSorted.MonthsToReturn = monthsToReturn;

      return this.rdoHttp.post('metrics/summary/chart', pagedSorted).pipe(
        map((p) => {
          return (p as any).Items;
        })
      );
    }
  };

  public getWeeklyChartData = (
    monthsToReturn: number,
    useRouseSchema: boolean
  ): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    const latestAvailableMonthId =
      this.filterInfoService.getLatestAvailableMonthId();
    let monthIdStart = 0;
    let monthIdEnd = 0;
    [monthIdStart, monthIdEnd] =
      filterFunctions.getWeeklyChartBeginAndEndMonthIds(
        monthsToReturn,
        this.filterInfoService.activeFilterService.dateOptions[0].id
      );

    if (this.isGetRequest) {
      // for some reason api endpoint requires these
      const parameters = new QueryParams();

      parameters.set('page', '1');
      parameters.set('pageSize', '500');
      parameters.set('MonthIdEnd', monthIdEnd);
      parameters.set('MonthIdStart', monthIdStart);

      if (useRouseSchema) {
        parameters.set('UseRouseSchema', 'true');
      }
      // remove DateFrom and DateTo from global filter query so we can used cached version
      return (
        this.rdoHttp
          //.getfx(['MonthIDStart', 'MonthIDEnd'], 'metrics/summary/weekly-chart', { search: parameters })
          .getfx(
            ['MonthIDStart', 'MonthIDEnd'],
            'metrics/summary/weekly-chart',
            { search: parameters }
          )
          .pipe(
            map((p) => {
              return (p as any).Items;
            })
          )
      );
    } else {
      const pagedSorted = new SummaryWeeklyPagedQuery();
      pagedSorted.Page = 1;
      pagedSorted.PageSize = 500;
      //pagedSorted.MonthsToReturn = monthsToReturn;
      pagedSorted.MonthIdEnd = monthIdEnd;
      pagedSorted.MonthIdStart = monthIdStart;
      return this.rdoHttp
        .post('metrics/summary/weekly-chart', pagedSorted)
        .pipe(
          map((p) => {
            return (p as any).Items;
          })
        );
    }
  };

  public getOnRentAndInFleetData = (
    monthsToReturn: number,
    useRouseSchema: boolean
  ): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    if (this.isGetRequest) {
      // for some reason api endpoint requires these
      const parameters = new QueryParams();
      parameters.set('page', '1');
      parameters.set('pageSize', '500');
      parameters.set('MonthsToReturn', monthsToReturn.toString());
      if (useRouseSchema) {
        parameters.set('UseRouseSchema', 'true');
      }
      // remove DateFrom and DateTo from global filter query so we can used cached version
      return this.rdoHttp
        .getfx(
          ['MonthIDStart', 'MonthIDEnd'],
          'metrics/summary/onrent-infleet',
          { search: parameters }
        )
        .pipe(
          map((p) => {
            return (p as any).Items;
          })
        );
    } else {
      const pagedSorted = new SummaryPagedQuery();
      pagedSorted.Page = 1;
      pagedSorted.PageSize = 500;
      pagedSorted.MonthsToReturn = monthsToReturn;

      return this.rdoHttp
        .post('metrics/summary/onrent-infleet', pagedSorted)
        .pipe(
          map((p) => {
            return (p as any).Items;
          })
        );
    }
  };

  public getRateChangeData = (
    monthsToReturn: number,
    useRouseSchema: boolean,
    useRentedAs: boolean
  ): Observable<any> => {
    const useNewEndpoint = this.featureFlagService.isActive(
      'flagNewRateChangeEndpoint'
    );

    if (useNewEndpoint) {
      // 'https://metrics-api.stage.rouseanalytics.com/metrics/rate-change'
      const baseUrl = environment.metricsApiUrl;
      const url = `${baseUrl}/metrics/rate-change`;
      const pagedSorted = new SummaryPagedQuery();
      pagedSorted.Page = 1;
      pagedSorted.PageSize = 500;
      pagedSorted.MonthsToReturn = monthsToReturn;

      if (useRouseSchema) {
        pagedSorted.UseRouseSchema = true;
      }

      return defer(async () => {
        const params =
          await this.metricsApiService.applyFiltersToRequestBody(pagedSorted);

        /**
        https://rouseservices.atlassian.net/browse/RIE-14126
        We need to apply this UseRentedAsProductType here, because
        the filter values are applied onto the params (for some reason), which will
        override the UseRentedAsProductType value we want.
        The logic is that if the client has "rented as" data, UseRentedAsProductType should
        default to true, regardless of what the filter value is. If the user switches
        the product type column to remove rented as the primary product type, then we should
        send false.  If the client doesn't have "rented as", then UseRentedAsProductType will be false.
         */
        if (useRentedAs) {
          params.UseRentedAsProductType = true;
        }

        const result = await this.metricsApiService
          .performHttpRequest<any>(HTTP_WORD.POST, url, params)
          .pipe(map(normalizePercentNullsToZero))
          .toPromise();
        return result;
      });
    } else {
      this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);

      // const url = useNewEndpoint ? 'https://rdo-dataservice.stage.rouseanalytics.com/api/products/rate-change' : 'metrics/summary/rate-change';
      const url = 'metrics/summary/rate-change';
      if (this.isGetRequest) {
        // for some reason api endpoint requires these
        const parameters = new QueryParams();
        parameters.set('page', '1');
        parameters.set('pageSize', '500');
        parameters.set('MonthsToReturn', monthsToReturn.toString());

        if (useRouseSchema) {
          parameters.set('UseRouseSchema', 'true');
        }

        // remove DateFrom and DateTo from global filter query so we can used cached version
        return this.rdoHttp
          .getfx(['MonthIDStart', 'MonthIDEnd'], url, {
            search: parameters,
          })
          .pipe(
            map((p) => {
              return p as any;
            })
          );
      } else {
        const pagedSorted = new SummaryPagedQuery();
        pagedSorted.Page = 1;
        pagedSorted.PageSize = 500;
        pagedSorted.MonthsToReturn = monthsToReturn;

        return this.rdoHttp.post(url, pagedSorted).pipe(
          map((p) => {
            return p as any;
          })
        );
      }
    }
  };

  public getRateTypes = (): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    if (this.isGetRequest) {
      return this.rdoHttp.getf('metrics/summary/rate-types').pipe(
        map((p) => {
          return this.revenueMappingService.processRateTypesResponse(
            (p as any).Items[0]
          );
        })
      );
    } else {
      return this.rdoHttp.post('metrics/summary/rate-types').pipe(
        map((p) => {
          return this.revenueMappingService.processRateTypesResponse(
            (p as any).Items[0]
          );
        })
      );
    }
  };

  public getTopBranches = (): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    return this.viewService
      .getComparisonModeSortOptions(ComparisonModeSortOptions.Revenue)
      .pipe(
        switchMap((sorting) => {
          if (this.isGetRequest) {
            const params = new QueryParams();

            this.queryService.setSortingParams(params, sorting);

            return this.rdoHttp
              .getf('metrics/summary/branches', { search: params })
              .pipe(map((res) => res as any));
          } else {
            const pagedSorted = new PagedQuery();
            pagedSorted.SetSorting(sorting);
            pagedSorted.SetSorting(sorting);

            return this.rdoHttp
              .post('metrics/summary/branches', pagedSorted)
              .pipe(map((res) => res as any));
          }
        })
      );
  };

  public getTopDistricts = (): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    return this.viewService
      .getComparisonModeSortOptions(ComparisonModeSortOptions.Revenue)
      .pipe(
        switchMap((sorting) => {
          if (this.isGetRequest) {
            const params = new QueryParams();

            this.queryService.setSortingParams(params, sorting);

            return this.rdoHttp
              .getf('metrics/summary/districts', { search: params })
              .pipe(map((res) => res as any));
          } else {
            const pagedSorted = new PagedQuery();
            pagedSorted.SetSorting(sorting);
            return this.rdoHttp
              .post('metrics/summary/districts', pagedSorted)
              .pipe(map((res) => res as any));
          }
        })
      );
  };

  public getTopRegions = (): Observable<any> => {
    return this.viewService
      .getComparisonModeSortOptions(ComparisonModeSortOptions.Revenue)
      .pipe(
        switchMap((sorting) => {
          this.isGetRequest = filterFunctions.isGetRequest(
            this.filterInfoService
          );
          if (this.isGetRequest) {
            const params = new QueryParams();

            this.queryService.setSortingParams(params, sorting);
            return this.rdoHttp
              .getf('metrics/summary/regions', { search: params })
              .pipe(map((res) => res as any));
          } else {
            const pagedSorted = new PagedQuery();
            pagedSorted.SetSorting(sorting);
            return this.rdoHttp
              .post('metrics/summary/regions', pagedSorted)
              .pipe(map((res) => res as any));
          }
        })
      );
  };

  public getTopCategories = (): Observable<any> => {
    this.isGetRequest = filterFunctions.isGetRequest(this.filterInfoService);
    return this.viewService
      .getComparisonModeSortOptions(ComparisonModeSortOptions.Revenue)
      .pipe(
        switchMap((sorting) => {
          if (this.isGetRequest) {
            const params = new QueryParams();
            this.queryService.setSortingParams(params, sorting);
            return this.rdoHttp
              .getf('metrics/summary/categories', { search: params })
              .pipe(map((res) => res as any));
          } else {
            const pagedSorted = new PagedQuery();
            pagedSorted.SetSorting(sorting);
            return this.rdoHttp
              .post('metrics/summary/categories', pagedSorted)
              .pipe(map((res) => res as any));
          }
        })
      );
  };

  public getRevenueDistribution = (
    monthsToReturn: number,
    useRouseSchema: boolean
  ): Observable<any> => {
    return this.viewService
      .getComparisonModeSortOptions(ComparisonModeSortOptions.Revenue)
      .pipe(
        switchMap((sorting) => {
          if (this.isGetRequest) {
            const params = new QueryParams();
            params.set('MonthsToReturn', monthsToReturn.toString());
            if (useRouseSchema) {
              params.set('UseRouseSchema', 'true');
            }
            this.queryService.setSortingParams(params, sorting);

            return this.rdoHttp
              .getf('metrics/summary/revenue-distribution', { search: params })
              .pipe(map((res) => res as any));
          } else {
            const pagedSorted = new SummaryPagedQuery();
            pagedSorted.SetSorting(sorting);
            pagedSorted.MonthsToReturn = monthsToReturn;

            return this.rdoHttp
              .post('metrics/summary/revenue-distribution', pagedSorted)
              .pipe(map((res) => res as any));
          }
        })
      );
  };
}
